import { Link, useLocation } from "react-router-dom";
import {
  Activity, ArrowRightLeft, ArrowUpDown, Building2, CheckCircle, Code, Container, FileSearch, FileText, Flame, Home, Inbox, KeyRound,
  Landmark, PanelLeftClose, PanelLeftOpen, RefreshCw, ScrollText, Server, Shield,
  ShieldCheck, UserCog, Users, Wallet, Webhook, type LucideIcon,
} from "lucide-react";
import { BrandLockup, BrandMark } from "@/components/shell/brand-logo";
import { Avatar } from "@/components/ui/avatar";
import { Tooltip } from "@/components/ui/tooltip";
import type { NavSection } from "@/lib/constants";
import { useAuthStore } from "@/stores/auth-store";
import { useBranding } from "@/hooks/use-branding";
import { cn } from "@/lib/utils";

const ICONS: Record<string, LucideIcon> = {
  Home, Wallet, ArrowUpDown, CheckCircle, Shield, Activity, RefreshCw, FileText, Users, Container,
  Code, Landmark, KeyRound, UserCog, Server, ScrollText, Building2, ShieldCheck, Webhook, Flame, ArrowRightLeft,
  FileSearch, Inbox,
};

export default function Sidebar({
  sections,
  onNavigate,
  collapsed,
  onToggleCollapse,
}: {
  sections: NavSection[];
  onNavigate?: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}) {
  const pathname = useLocation().pathname;
  // Longest matching href wins, so /developer/api-keys doesn't also light up /developer.
  const activeHref = sections
    .flatMap((section) => section.items.map((item) => item.href))
    .filter((href) => pathname === href || pathname.startsWith(href + "/"))
    .sort((a, b) => b.length - a.length)[0];
  const orgLabel = useAuthStore((s) => s.experience.activeOrganization.label);
  const { logoUrl } = useBranding();
  const orgInitials = orgLabel.replace(/[^A-Za-z0-9 ]/g, "").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "—";
  return (
    <nav
      aria-label="Sidebar"
      className={cn(
        "flex h-full shrink-0 flex-col overflow-y-auto bg-sidebar-bg text-text transition-[width] duration-150",
        collapsed ? "w-[72px]" : "w-[272px]"
      )}
    >
      <div
        className={cn(
          "flex items-center",
          collapsed ? "flex-col gap-2.5 px-2 pb-[18px] pt-[22px]" : "min-h-[52px] justify-between px-[18px] pb-[18px] pt-[22px]"
        )}
      >
        <div className="flex min-w-0 flex-1 items-center">
          {/* Collapsed rail has room for the mark only; expanded gets the full
              lockup from one asset, so the wordmark is the real artwork rather
              than a text node that can drift from it. */}
          {collapsed ? <BrandMark size={40} /> : <BrandLockup />}
        </div>
        <Tooltip content={collapsed ? "Expand sidebar" : "Collapse sidebar"}>
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            data-testid="sidebar-toggle"
            className="grid size-8 shrink-0 place-items-center rounded-md text-muted transition-colors hover:bg-disabled-soft hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft"
          >
            {collapsed ? <PanelLeftOpen aria-hidden className="size-5" /> : <PanelLeftClose aria-hidden className="size-5" />}
          </button>
        </Tooltip>
      </div>

      <div className={cn("flex-1 pb-3 pt-1", collapsed ? "px-2" : "px-4")}>
        {sections.map((section) => (
          <div key={section.label} className="mb-4">
            {!collapsed && (
              // Drawn for every section, including single-item ones ("Treasury",
              // "Account"): the heading is the only thing that names those groups,
              // and the shell tests pin every visible section heading as rendered.
              <div className="px-2 pb-1.5 pt-4 text-sm first:pt-2 font-medium text-text">{section.label}</div>
            )}
            <ul
              className={cn(
                section.items.length === 1 && !collapsed ? "pt-3 first:pt-0" : collapsed ? "pt-2 first:pt-0" : undefined
              )}
            >
              {section.items.map((item) => {
                const Icon = ICONS[item.icon] ?? Shield;
                const active = item.href === activeHref;
                return (
                  <li key={item.href}>
                    <Link
                      to={item.href}
                      onClick={() => onNavigate?.()}
                      aria-current={active ? "page" : undefined}
                      title={collapsed ? item.label : undefined}
                      className={cn(
                        "relative flex h-9 w-full items-center gap-2 rounded-md text-left text-sm font-medium text-text transition-colors hover:bg-disabled-soft hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft",
                        collapsed ? "justify-center px-0" : "px-2",
                        active && "bg-disabled-soft font-semibold text-ink"
                      )}
                    >
                      <Icon aria-hidden className="size-5 shrink-0" />
                      {!collapsed && <span className="truncate">{item.label}</span>}
                      {item.badge ? (
                        <span
                          className={cn(
                            "grid h-[18px] min-w-[18px] place-items-center rounded-full bg-danger-100 px-1 text-xs font-medium text-danger",
                            collapsed ? "absolute right-0.5 top-0.5" : "ml-auto"
                          )}
                        >
                          {item.badge}
                        </span>
                      ) : null}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      <div className={cn("border-t border-line/60", collapsed ? "px-2 py-3" : "px-6 py-3")}>
        <div className={cn("flex items-center", collapsed ? "justify-center" : "gap-2.5")}>
          <span title={orgLabel}>
            {logoUrl ? (
              <span className="grid size-10 shrink-0 place-items-center overflow-hidden rounded border border-line bg-bg p-[3px]" aria-label={`${orgLabel} logo`}>
                <img src={logoUrl} alt="" className="size-full object-contain" />
              </span>
            ) : (
              <Avatar initials={orgInitials} bgColor="var(--ads-org-avatar)" size={32} fontSize={12} aria-label={`${orgLabel} logo`} />
            )}
          </span>
          {!collapsed && (
            <div className="min-w-0 flex-1 leading-tight">
              <div className="truncate text-xs font-semibold text-ink">{orgLabel}</div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}